import { Dialog, LinearProgress } from "@mui/material";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import { Icon } from "@iconify/react";
import {
  CellClassParams,
  CellEditorSelectorResult,
  ColDef,
  GridApi,
  ICellEditorParams,
  ValueFormatterParams,
  ValueGetterParams,
  ValueSetterParams,
} from "ag-grid-community";
import _ from "lodash";
import { AgGridReact } from "ag-grid-react";
import { getGrantTemplate } from "../../api/Esop";
import { Label } from "../../components/shared/InputField";
import Tooltip from "../../components/shared/Tooltip";
import {
  Box,
  ButtonPrimary,
  ButtonPrimary1,
  CellRenderer,
  HStack,
  ValidityCellRenderer,
  VStack,
} from "../../components/utils";
import {
  useAddGrantExcel,
  useEmployees,
  useEsopPlans,
  useVestingTemplates,
} from "../../queries";
import {
  useGetGrantLetterSettings,
  useGetGrantLetterTemplates,
} from "../../queries/siteSettings";
import {
  usePermissionStore,
  useCompanyStore,
  useGrantImportStore,
} from "../../store";
import { determineUserAccessToResource } from "../../utils/auth";
import { Action, Resource } from "../../utils/interfaces/Companies";
import { ValidationObj } from "../../types/Grant";
import {
  convertBase64ToBlob,
  downloadBlobObject,
} from "../../utils/DownloadFile";
import convertToBase64 from "../../utils/convertToBase64";
import { ValidationResponse } from "../../types/Employee";
import { formatDisplayDate } from "../../utils/date";
import { Instructions } from "../../components/shared/Instructions";
import BulkUploadTemplatesDialog from "../../modals/BulkUploadTemplatesDialog";

const HIDDEN_COLUMNS = ["_grantId"];

interface UploadResult {
  validationData: ValidationObj[][];
  hasErrors: boolean;
  message: string;
}

function GrantsImport() {
  const { companyData } = useCompanyStore();
  const { mutate: addGrantExcel, isLoading: addingGrantExcel } =
    useAddGrantExcel();

  const { data: _employeeData } = useEmployees();
  const { data: _allPlans } = useEsopPlans();
  const { data: _vestingTemplates } = useVestingTemplates();
  const { data: _grantLetterTemplates } = useGetGrantLetterTemplates();
  const { data: grantSettings } = useGetGrantLetterSettings();

  const [showStatus, setShowStatus] = useState(false);
  const {
    importResult,
    setImportResult,
    clearImportResult,
    pendingAssignments,
  } = useGrantImportStore();
  const [hasErrors, setHasErrors] = useState(false);
  const [operationInProgress, setOperationInProgress] = useState(false);
  const [canSelectedBeResubmitted, setCanSelectedBeResubmitted] =
    useState(false);
  const [selectedGrantIdentifiers, setSelectedGrantIdentifiers] = useState<
    string[]
  >([]);

  const gridApi = useRef<GridApi | any>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const navigate = useNavigate();

  const [uploadTemplateOpen, setUploadTemplateOpen] = useState(false);

  useEffect(() => {
    clearImportResult();
  }, []);

  const resetFlow = useCallback(() => {
    if (fileRef.current) fileRef.current.value = "";
    clearImportResult();
    setHasErrors(false);
    setCanSelectedBeResubmitted(false);
    setSelectedGrantIdentifiers([]);
  }, [clearImportResult]);

  const onGridReady = (params: any) => {
    gridApi.current = params.api;
    params.api.sizeColumnsToFit();
    params.api.resetRowHeights();
  };

  const checkErrors = (data: ValidationResponse[][], setErrors = true) => {
    const err = data
      .filter((dataItem) =>
        dataItem.find((dataEntity) => dataEntity.name !== "validity")
      )
      .some((dataItem) => dataItem.some((item) => item.valid === false));
    if (setErrors) setHasErrors(err);
    return err;
  };

  function isRowValid(rowData: ValidationResponse[]) {
    return !rowData
      ?.filter((d) => d.name !== "validity")
      ?.some((d) => !d.valid);
  }

  function handleUploadResult(data: {
    validationData?: ValidationObj[][];
    hasErrors?: boolean;
    message?: string;
  }) {
    const result: UploadResult = {
      validationData: data.validationData ?? [],
      hasErrors: data.hasErrors ?? true,
      message: data.message ?? "",
    };
    setImportResult(result);
    if (!result.hasErrors) {
      setHasErrors(false);
    } else {
      checkErrors(result.validationData, true);
    }
  }

  function handleChange(e: any) {
    const file = e.target?.files[0];
    if (!file) return;
    const allowedFileExtensions = ["xlsx", "xls", "ods"];
    if (allowedFileExtensions.some((ext) => file?.name.endsWith(ext))) {
      setShowStatus(true);
      convertToBase64(file).then((data) => {
        addGrantExcel(
          { file: data as string },
          {
            onSuccess: (response) => {
              setShowStatus(false);
              handleUploadResult(response);
            },
            onError: (error) => {
              setShowStatus(false);
              if (fileRef.current) fileRef.current.value = "";
              const errorData = (error as any).response?.data?.data;
              if (errorData?.validationData) {
                handleUploadResult(errorData);
              } else if (errorData?.message) {
                toast(errorData.message, { type: "error" });
              }
            },
          }
        );
      });
    } else {
      toast(
        `Invalid file type, allowed file types are ${allowedFileExtensions.join(
          ", "
        )}`,
        { type: "error" }
      );
      e.target.value = "";
    }
  }

  const proceedToResubmit = (all = true) => {
    setOperationInProgress(true);
    let data: ValidationResponse[][] = [];
    if (all) {
      gridApi.current?.forEachNode((node: any) => {
        data.push(node.data);
      });
    } else {
      const selectedRows = gridApi.current?.getSelectedRows();
      if ((selectedRows ?? []).length === 0) {
        toast("No Rows Selected", { autoClose: 2000, type: "error" });
        setOperationInProgress(false);
        return;
      }
      data = selectedRows;
    }
    if (data.length > 0) {
      data.forEach((dataItem) => {
        dataItem.forEach((item) => {
          if (item.name === "Options Granted" || item.name === "Grant Price") {
            item.value = Number(item.value);
          }
        });
      });
      addGrantExcel(
        { file: "", correctedGrantData: data },
        {
          onSuccess: (response) => {
            setOperationInProgress(false);
            handleUploadResult(response);
          },
          onError: (error) => {
            setOperationInProgress(false);
            const errorData = (error as any).response?.data?.data;
            if (errorData?.validationData) {
              handleUploadResult(errorData);
            } else if (errorData?.message) {
              toast(errorData.message, { type: "error" });
            }
          },
        }
      );
    } else {
      setOperationInProgress(false);
    }
  };

  const handleConfirmGrants = () => {
    setOperationInProgress(true);
    const confirmedData = (importResult?.validationData ?? []).map(
      (dataItem) => {
        dataItem.forEach((item) => {
          if (item.name === "Options Granted" || item.name === "Grant Price") {
            item.value = Number(item.value);
          }
        });
        return dataItem;
      }
    );
    addGrantExcel(
      {
        correctedGrantData: confirmedData,
        confirm: true,
      },
      {
        onSuccess: (response: any) => {
          setOperationInProgress(false);
          toast(response?.message ?? "Grants created successfully", {
            type: "success",
          });
          clearImportResult();
          navigate("/options/allPlans");
        },
        onError: (error: any) => {
          setOperationInProgress(false);
          if (error?.response?.status === 400) {
            const msg =
              error?.response?.data?.errorMessage ??
              "Session expired. Please re-upload your file.";
            resetFlow();
            toast(msg, { type: "error" });
          } else {
            toast("Failed to create grants. Please try again.", {
              type: "error",
            });
          }
        },
      }
    );
  };

  const downloadTemplate = () => {
    getGrantTemplate().then((uploadTemplate) => {
      const template = uploadTemplate.data.uploadTemplate;
      convertBase64ToBlob(
        template,
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
      ).then((blobObject) => {
        downloadBlobObject(
          blobObject,
          `${companyData?.name} Grant Upload Template.xlsx`
        );
      });
    });
  };

  const employees = useMemo(
    () =>
      _employeeData?.map((employee) => employee.employeeIdentificationString),
    [_employeeData]
  );
  const plans = useMemo(
    () => _allPlans?.map((plan) => plan.planName),
    [_allPlans]
  );
  const vestingTemplates = useMemo(
    () => _vestingTemplates?.map((template) => template.vestingTemplateName),
    [_vestingTemplates]
  );
  const grantLetterTemplateNames = useMemo(() => {
    const templates = Array.isArray(_grantLetterTemplates)
      ? _grantLetterTemplates
      : (_grantLetterTemplates as any)?.grantLetterTemplates ?? [];
    return (templates as { templateName?: string }[])
      .map((t) => t.templateName)
      .filter(Boolean) as string[];
  }, [_grantLetterTemplates]);

  const isPreviewMode =
    importResult !== null && importResult.hasErrors === false;

  const getCellStyle = (params: CellClassParams<any, any>) => {
    const colId = params.column.getColId();
    if (colId === "validity") {
      if (params.data.find((dataItem: { valid: boolean }) => !dataItem.valid))
        return { color: "red", backgroundColor: "unset" };
      return { color: "black", backgroundColor: "unset" };
    }
    return params?.value?.valid
      ? { color: "black", backgroundColor: "unset" }
      : { color: "red", backgroundColor: "unset" };
  };

  function setValueAndResetCell(params: ValueSetterParams) {
    const dataItem = params?.data;
    if (params.newValue === undefined || params.newValue === null) return false;
    dataItem.forEach((data: any) => {
      if (data.name === params.colDef.field) {
        if (data.value !== params.newValue) {
          data.value = params.newValue;
          data.valid = true;
          data.error = null;
        }
      }
    });
    params.node?.setData(dataItem);
    const data: any[] = [];
    gridApi.current?.forEachNode((node: any) => {
      data.push(node.data);
    });
    checkErrors(data);
    gridApi.current?.refreshCells();
    return true;
  }

  const valueGetter = (params: ValueGetterParams<any, any>) => {
    const colId = params.column.getColId();
    const data = params?.data?.find(
      (dataItem: any) => dataItem.name === colId
    ) || { value: undefined, valid: true };
    if (colId === "validity") {
      const isRowDataValid = isRowValid(params?.data);
      const validityValue: ValidationResponse = {
        name: "validity",
        valid: isRowDataValid,
        value: isRowDataValid ? "Valid" : "Invalid",
        error: isRowDataValid ? null : "Validation Error",
      };
      return validityValue;
    }
    if (
      (colId === "Date Of Grant" || colId === "Vesting Date") &&
      data?.value
    ) {
      data.value = formatDisplayDate(data?.value as unknown as string);
    }
    return data;
  };

  const cellEditorSelector: (
    params: ICellEditorParams<any>
  ) => CellEditorSelectorResult | undefined = (
    params: ICellEditorParams<any>
  ) => {
    const field = params.colDef.field;
    switch (field?.toLowerCase()) {
      case "employee id":
        return {
          component: "agRichSelectCellEditor",
          params: {
            allowTyping: true,
            filterList: true,
            highlightMatch: true,
            valueListMaxWidth: 150,
            values: employees || [],
          },
        };
      case "plan name":
        return {
          component: "agRichSelectCellEditor",
          params: {
            allowTyping: true,
            filterList: true,
            highlightMatch: true,
            valueListMaxWidth: 150,
            values: plans || [],
          },
        };
      case "vesting template":
        return {
          component: "agRichSelectCellEditor",
          params: {
            allowTyping: true,
            filterList: true,
            highlightMatch: true,
            valueListMaxWidth: 150,
            values: vestingTemplates || [],
          },
        };
      case "vesting date type":
        return {
          component: "agRichSelectCellEditor",
          params: {
            allowTyping: true,
            filterList: true,
            highlightMatch: true,
            valueListMaxWidth: 150,
            values: ["GrantDate", "EmployeeJoiningDate", "CustomDate"],
          },
        };
      case "grant letter template":
        return {
          component: "agRichSelectCellEditor",
          params: {
            allowTyping: true,
            filterList: true,
            highlightMatch: true,
            valueListMaxWidth: 200,
            values: grantLetterTemplateNames,
          },
        };
      case "date of grant":
      case "vesting date":
        return {
          component: "agDateCellEditor",
          params: {
            allowTyping: true,
            filterList: true,
            highlightMatch: true,
            valueListMaxWidth: 150,
            min: "1900-01-01",
            max: "2100-12-31",
          },
        };
      default:
        return undefined;
    }
  };

  const valueFormatter = (params: ValueFormatterParams<any, any>) => {
    const item = params.column.getColId();
    if (item === "Date Of Grant" || item === "Vesting Date") {
      if (!params.value.value) return "";
      return formatDisplayDate(params.value.value as unknown as string);
    }
    return "";
  };

  const [validationData, columnNames] = useMemo(() => {
    if (
      !importResult ||
      !importResult.validationData ||
      importResult.validationData.length === 0
    ) {
      return [[], []];
    }
    const processedData = importResult.validationData.map((dataItem) => {
      dataItem.forEach((data) => {
        if (
          ["Date Of Grant", "Vesting Date"].includes(data.name) &&
          data.value
        ) {
          data.value = new Date(data.value).toDateString();
        }
      });
      return [...dataItem];
    });
    // Build column names robustly: prefer the row with most keys, fallback to first row
    const keysRow =
      _.maxBy(processedData, (r) => (r ? Object.keys(r).length : 0)) ||
      processedData[0];
    let cols: string[] = [];
    if (keysRow) {
      cols = [
        "validity",
        ...Object.values(keysRow || {})
          .map((value: any) => value?.name as string)
          .filter((name) => !!name && !HIDDEN_COLUMNS.includes(name)),
      ];
    }
    return [processedData, cols];
  }, [importResult]);

  const defaultColDef = useMemo<ColDef>(
    () => ({
      checkboxSelection: true,
      flex: 1,
      autoHeight: true,
      initialWidth: 150,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      editable: true,
      columnsMenuParams: {
        suppressColumnFilter: true,
      },
      filterParams: {
        buttons: ["reset"],
        maxNumConditions: 5,
      },
      minWidth: 150,
      filter: true,
      resizable: true,
    }),
    [isPreviewMode]
  );

  const columnDefs: ColDef[] = useMemo(
    () =>
      columnNames?.map((item, index) => {
        const colDef: ColDef = {
          headerName: item,
          checkboxSelection: index === 0,
          headerCheckboxSelection: index === 0,
          floatingFilter: true,
          valueGetter,
          field: item,
          cellRenderer:
            item === "validity" ? ValidityCellRenderer : CellRenderer,
          cellRendererParams: ({ value }: { value: any }) => value,
          cellEditorSelector,
          cellStyle: getCellStyle,
          valueFormatter,
          valueSetter: setValueAndResetCell,
          filterValueGetter: (params) => {
            const data = params.data?.find(
              (dataItem: any) => dataItem.name === item
            );
            if (item === "validity") {
              return isRowValid(params?.data || []) ? "Valid" : "Invalid";
            }
            return data?.value;
          },
          cellEditorParams: (params: any) => {
            const field = params.colDef.field;
            if (
              (field === "Date Of Grant" || field === "Vesting Date") &&
              params.value.value
            ) {
              return { ...params.value, value: new Date(params.value.value) };
            }
            return params.value;
          },
          headerClass: "text-sm font-normal capitalize",
          cellDataType:
            item === "Date Of Grant" || item === "Vesting Date"
              ? "date"
              : "string",
          pinned:
            item === "validity" || item === "Employee Id" ? "left" : undefined,
        };
        return colDef;
      }),
    [columnNames, isPreviewMode, grantLetterTemplateNames]
  );

  useEffect(() => {
    if (!importResult) return;
    if (!importResult.hasErrors) {
      setHasErrors(false);
    } else {
      checkErrors(importResult.validationData, true);
    }
  }, [importResult]);

  return (
    <VStack className="justify-between w-full h-full px-4 bg-white border-2 rounded-md min-h-[600px]">
      <VStack>
        <HStack className="py-4 text-lg font-medium text-left bg-white border-b">
          <h6 className="flex-1">Upload Grants</h6>
          <ButtonPrimary onClick={downloadTemplate}>
            Download Template
          </ButtonPrimary>
        </HStack>
        <VStack className="w-full h-full gap-6 px-2 py-4 bg-white">
          {showStatus && (
            <VStack className="justify-between w-full h-full gap-6 px-2 py-4 my-auto bg-white">
              <LinearProgress className="w-full" variant="indeterminate" />
              <HStack className="justify-between w-full">
                <p className="text-sm font-normal text-center">
                  Please wait while we validate the data
                </p>
              </HStack>
            </VStack>
          )}

          {!showStatus && !importResult && (
            <>
              <Box className="flex justify-center w-full px-10 text-lg font-medium">
                <h6 className="flex justify-between">
                  Upload your Grants Excel{" "}
                  <Tooltip
                    text={`Upload your excel sheet containing grant data`}
                    _className="w-[400px]"
                  >
                    <Icon
                      icon="material-symbols:info-outline-rounded"
                      height={24}
                      width={24}
                      className="w-full ml-2 text-xs font-medium rounded cursor-pointer text-slate-dark"
                    />
                  </Tooltip>
                </h6>
              </Box>
              <HStack className="gap-8 pt-8 mx-auto">
                <div className="flex-1">
                  <Label className="text-sm font-normal">Excel File</Label>
                  <input
                    ref={fileRef}
                    className="w-full form-input"
                    type="file"
                    accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                    onChange={handleChange}
                  />
                  <span className="py-2 text-xs italic text-gray-500">
                    You can download the template for uploading grants{" "}
                    <a
                      className="text-blue-500 underline cursor-pointer"
                      onClick={downloadTemplate}
                    >
                      here
                    </a>
                  </span>
                </div>
              </HStack>
            </>
          )}

          {validationData && validationData.length > 0 && (
            <VStack>
              {isPreviewMode ? (
                <p className="px-1 mb-1 text-sm text-gray-500">
                  Preview of grants to be created. Select rows to upload grant
                  letter templates, then confirm to create grants.
                </p>
              ) : (
                <p className="px-1 mb-1 text-sm text-red-500">
                  Validation errors found. Correct the highlighted fields and
                  re-validate
                </p>
              )}

              <div className="py-4 ag-theme-material" style={{ height: 500 }}>
                <AgGridReact
                  key={`GRANTIMNPORTGRID_${isPreviewMode ? "preview" : "edit"}`}
                  columnDefs={columnDefs}
                  defaultColDef={defaultColDef}
                  onGridReady={onGridReady}
                  rowSelection="multiple"
                  rowData={validationData}
                  rowClass="border-t border-dashed"
                  suppressRowClickSelection={true}
                  onSelectionChanged={() => {
                    const selectedRows: ValidationObj[][] =
                      gridApi.current?.getSelectedRows() ?? [];
                    if (isPreviewMode) {
                      const identifiers = selectedRows
                        .map((row) => {
                          const item = row.find(
                            (i) => i.name === "GRANT IDENTIFIER"
                          );
                          return item?.value as string;
                        })
                        .filter(Boolean);
                      setSelectedGrantIdentifiers(identifiers);
                    } else {
                      if (selectedRows.length === 0) {
                        setCanSelectedBeResubmitted(false);
                        return;
                      }
                      const err = checkErrors(
                        selectedRows as ValidationResponse[][],
                        false
                      );
                      setCanSelectedBeResubmitted(!err);
                    }
                  }}
                />
              </div>

              {grantSettings?.disableAutoGrantLetterGeneration && (
                <VStack className="p-4 text-black-501 bg-yellow-100 rounded">
                  <HStack className="pt-0 justify-start">
                    <HStack className="gap-1 pl-1 items-start">
                      <Icon
                        icon="material-symbols:info-outline-rounded"
                        height={18}
                        width={18}
                        className="w-full text-xs font-medium text-black-501 rounded"
                      />
                      <p className="text-sm">Note</p>
                      <p> : </p>
                      <p className="text-xs pt-0.5 whitespace-nowrap">
                        Auto grant letter generation is currently turned off.
                        Grant letters will not be generated automatically upon
                        grant creation.
                      </p>
                    </HStack>
                  </HStack>
                </VStack>
              )}

              <HStack className="justify-between gap-4 p-3">
                <ButtonPrimary1
                  type="reset"
                  className="text-red-500"
                  onClick={resetFlow}
                >
                  Discard
                </ButtonPrimary1>
                <HStack className="justify-end gap-4">
                  {isPreviewMode ? (
                    <>
                      <ButtonPrimary1
                        onClick={() => {
                          const { permission } = usePermissionStore.getState();
                          const allowed = determineUserAccessToResource(
                            permission?.aclList || [],
                            Resource.Grant,
                            Action.All
                          );
                          if (!allowed) {
                            toast(
                              "You don't have permission to upload grant templates.",
                              { type: "error" }
                            );
                            return;
                          }
                          setUploadTemplateOpen(true);
                        }}
                      >
                        Upload Templates
                      </ButtonPrimary1>
                      {!operationInProgress && !addingGrantExcel ? (
                        <ButtonPrimary onClick={handleConfirmGrants}>
                          Confirm &amp; Create Grants
                        </ButtonPrimary>
                      ) : (
                        <Icon
                          className="animate-spin text-orange-501"
                          icon="lucide:loader-2"
                          width={36}
                        />
                      )}
                    </>
                  ) : (
                    <>
                      {!operationInProgress && (
                        <ButtonPrimary
                          disabled={hasErrors}
                          onClick={() => proceedToResubmit()}
                        >
                          Validate All
                        </ButtonPrimary>
                      )}
                      {!operationInProgress && (
                        <ButtonPrimary
                          disabled={!canSelectedBeResubmitted}
                          onClick={() => proceedToResubmit(false)}
                        >
                          Validate Selected
                        </ButtonPrimary>
                      )}
                      {operationInProgress && (
                        <Icon
                          className="animate-spin text-orange-501"
                          icon="lucide:loader-2"
                          width={36}
                        />
                      )}
                    </>
                  )}
                </HStack>
              </HStack>
            </VStack>
          )}
        </VStack>
      </VStack>
      <BulkUploadTemplatesDialog
        open={uploadTemplateOpen}
        onClose={() => setUploadTemplateOpen(false)}
        title="Upload Templates"
        onSuccess={() => gridApi.current?.refreshCells({ force: true })}
        bodyExtras={
          <Instructions
            data={[
              "Upload .docx or .zip files only",
              "Valid files will be saved as templates",
              "Invalid files will be skipped",
              "An upload summary will be shown after each upload",
            ]}
          />
        }
      />
    </VStack>
  );
}

export default GrantsImport;
